import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { ICollaborateur } from 'src/app/shared/models/collaborateur';
import { IPoste } from 'src/app/shared/models/poste';
import { CollaborateurService } from '../collaborateur.service';
import { PosteService } from '../../postes-list/poste.service';

@Component({
  selector: 'app-collaborateur-dialog',
  templateUrl: './collaborateur-dialog.component.html',
  styleUrls: ['./collaborateur-dialog.component.less']
})
export class CollaborateurDialogComponent implements OnInit {

  form!: UntypedFormGroup;
  isEditMode: boolean;
  isSubmitting = false;
  backendError: string | null = null;

  postes: IPoste[] = [];
  postesLoading = false;

  cvFile: File | null = null;

  constructor(
    private _fb: UntypedFormBuilder,
    private collaborateurService: CollaborateurService,
    private posteService: PosteService,
    private snackbarService: SnackbarService,
    public dialogRef: MatDialogRef<CollaborateurDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICollaborateur | null
  ) {
    this.isEditMode = data != null;
  }

  ngOnInit(): void {
    this.form = this._fb.group({
      nom: [this.data?.nom ?? '', [Validators.required, Validators.maxLength(120)]],
      prenom: [this.data?.prenom ?? '', [Validators.required, Validators.maxLength(120)]],
      commentaire: [this.data?.commentaire ?? ''],
      posteIds: [this.data?.postes?.map(p => p.posteId) ?? []]
    });

    this.loadPostes();
  }

  private loadPostes(): void {
    this.postesLoading = true;
    this.posteService.getAll().subscribe({
      next: (postes) => {
        // Un poste inactif déjà affecté au collaborateur reste visible pour ne pas le perdre en modification
        this.postes = postes;
        this.postesLoading = false;
      },
      error: () => {
        this.postesLoading = false;
      }
    });
  }

  onCvSelected(file: File): void {
    this.cvFile = file;
  }

  get cvIsRequired(): boolean {
    return !this.isEditMode && !this.cvFile;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // CDC §4.1 : le CV est obligatoire à la création
    if (!this.isEditMode && !this.cvFile) {
      this.backendError = 'Le CV est obligatoire à la création d\'un collaborateur.';
      return;
    }

    this.backendError = null;
    this.isSubmitting = true;

    const { nom, prenom, commentaire, posteIds } = this.form.value;
    const metadata = { nom, prenom, commentaire, posteIds };

    const action$ = this.isEditMode
      ? this.collaborateurService.update(this.data!.collabId, metadata, this.cvFile)
      : this.collaborateurService.create(metadata, this.cvFile!);

    action$.subscribe({
      next: (result) => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar(
          this.isEditMode ? 'Collaborateur modifié avec succès' : 'Collaborateur créé avec succès'
        );
        this.dialogRef.close(result);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(
          err,
          this.isEditMode ? 'Erreur lors de la modification' : 'Erreur lors de la création'
        );
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private extractErrorMessage(err: any, fallback: string): string {
    // 415/422/413 : erreurs de validation du CV (CDC §10.4)
    const body = err?.error;
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    if (err?.status === 413) return 'Le fichier dépasse la taille maximale autorisée (5 Mo).';
    if (err?.status === 415) return 'Le fichier doit être un PDF.';
    if (err?.status === 422) return 'Le PDF est invalide ou potentiellement dangereux.';
    return fallback;
  }
}
